<template>
  <Dialog
    :open="open"
    title="Create New Organization"
    size="md"
    @update:open="$emit('update:open', $event)"
  >
    <div class="space-y-4">
      <div
        class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900"
      >
        <BuildingOfficeIcon class="h-6 w-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
      </div>
      <div class="text-center">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Organizations allow you to manage teams, shared resources, and billing.
        </p>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Organization Name</label
          >
          <input
            type="text"
            id="name"
            v-model="form.name"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="e.g. Acme Corp"
            required
          />
          <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
        </div>

        <div>
          <label for="domain" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Domain (optional)</label
          >
          <div class="mt-1 flex rounded-md shadow-sm">
            <span
              class="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 text-gray-500 sm:text-sm"
            >
              https://
            </span>
            <input
              type="text"
              id="domain"
              v-model="form.domain"
              class="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="example.com"
            />
          </div>
          <p v-if="errors.domain" class="mt-1 text-sm text-red-600">{{ errors.domain }}</p>
        </div>
      </form>
    </div>

    <template #footer>
      <div class="sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" full-width :loading="isLoading" class="sm:col-start-2" @click="handleSubmit">
          Create
        </Button>
        <Button
          type="button"
          variant="outline"
          full-width
          class="mt-3 sm:col-start-1 sm:mt-0"
          @click="$emit('update:open', false)"
        >
          Cancel
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, reactive } from 'vue';
  import { BuildingOfficeIcon } from '@heroicons/vue/24/outline';
  import { useOrganizationsStore } from '@/stores/organizations';
  import { createOrganizationSchema } from '../validation';
  import { z } from 'zod';
  import { Dialog, Button } from '@asabdev/asab-ui';

  defineProps<{
    open: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'created', id: string): void;
  }>();

  const orgStore = useOrganizationsStore();
  const isLoading = ref(false);
  const errors = reactive<Record<string, string>>({});

  const form = reactive({
    name: '',
    domain: '',
  });

  const handleSubmit = async () => {
    try {
      isLoading.value = true;
      Object.keys(errors).forEach(key => delete errors[key]);

      // Validate
      createOrganizationSchema.parse(form);

      const result = await orgStore.createOrganization(form);
      if (result) {
        emit('created', result.id);
        emit('update:open', false);
        form.name = '';
        form.domain = '';
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        err.errors.forEach(e => {
          if (e.path[0]) {
            errors[e.path[0] as string] = e.message;
          }
        });
      } else {
        // API error — show on the name field since that's what causes slug conflicts
        const axiosErr = err as { response?: { data?: { error?: string } } };
        errors.name = axiosErr?.response?.data?.error || orgStore.error || 'Failed to create organization';
      }
    } finally {
      isLoading.value = false;
    }
  };
</script>
